import { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Landing from '@/pages/Landing';
import Auth from '@/pages/Auth';
import Dashboard from '@/pages/Dashboard';
import Profile from '@/pages/Profile';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import KVKK from '@/pages/KVKK';
import Feedback from '@/pages/Feedback';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);

  // Setup axios interceptor for 401 errors (user deleted)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && error.response?.data?.detail === "User not found") {
          // User was deleted by admin
          toast.error('Hesabınız yönetici tarafından silindi. Çıkış yapılıyor...');
          setTimeout(() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setIsAuthenticated(false);
            setUser(null);
            window.location.href = '/';
          }, 2000);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setIsAuthenticated(true);
      setUser(JSON.parse(storedUser));
    }
    
    const adminToken = localStorage.getItem('admin_token');
    const storedAdmin = localStorage.getItem('admin');
    if (adminToken && storedAdmin) {
      setIsAdminAuthenticated(true);
      setAdmin(JSON.parse(storedAdmin));
    }
    
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  const handleAdminLogin = (token, adminData) => {
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin', JSON.stringify(adminData));
    setIsAdminAuthenticated(true);
    setAdmin(adminData);
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin');
    setIsAdminAuthenticated(false);
    setAdmin(null);
  };

  if (loading) {
    return <div className="min-h-screen bg-neutral-50 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Admin Routes */}
          <Route
            path="/kadir"
            element={
              isAdminAuthenticated ? (
                <Navigate to="/kadir/dashboard" replace />
              ) : (
                <AdminLogin onAdminLogin={handleAdminLogin} />
              )
            }
          />
          <Route
            path="/kadir/dashboard"
            element={
              isAdminAuthenticated ? (
                <AdminDashboard admin={admin} onLogout={handleAdminLogout} />
              ) : (
                <Navigate to="/kadir" replace />
              )
            }
          />

          {/* User Routes */}
          <Route
            path="/auth"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Auth onLogin={handleLogin} />
              )
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Landing />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/profile"
            element={
              isAuthenticated ? (
                <Profile user={user} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          
          {/* KVKK - Public Route */}
          <Route path="/kvkk" element={<KVKK />} />
          <Route path="/geribildirim" element={<Feedback />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
